import { useEffect } from 'react';
import { ANIMATION_KEYFRAMES_CSS } from '@/components/inspector-fields/AnimationSettings/keyframes';

const STYLE_ID = 'b4-animation-keyframes-preview';
const PENDING_CLASS = 'b4-anim-pending';
const INTERSECTION_THRESHOLD = 0.15;

const ANIMATION_KEYFRAME_MAP: Record<string, string> = {
  fadeIn: 'b4-fadeIn',
  slideUp: 'b4-slideUp',
  slideDown: 'b4-slideDown',
  slideLeft: 'b4-slideLeft',
  slideRight: 'b4-slideRight',
  zoomIn: 'b4-zoomIn',
  zoomOut: 'b4-zoomOut',
  flipUp: 'b4-flipUp',
  bounceIn: 'b4-bounceIn',
  blurIn: 'b4-blurIn',
  revealLeft: 'b4-revealLeft',
  revealRight: 'b4-revealRight',
  revealUp: 'b4-revealUp',
  revealDown: 'b4-revealDown',
};

/**
 * Sets up IntersectionObserver-based scroll animations in preview mode.
 * When preview mode is enabled, injects keyframe CSS, adds the pending class
 * to animated elements, and observes them for viewport entry.
 */
export const usePreviewAnimations = (previewMode: boolean) => {
  useEffect(() => {
    if (!previewMode) return;

    // Inject keyframe styles
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent =
        ANIMATION_KEYFRAMES_CSS + `\n.${PENDING_CLASS} { opacity: 0 !important; }`;
      document.head.appendChild(style);
    }

    // Small delay to let React re-render with preview context before querying
    const timeoutId = setTimeout(() => {
      // Apply stagger delays before observing
      const staggerContainers = document.querySelectorAll('[data-stagger-delay]');
      staggerContainers.forEach((container) => {
        const staggerDelay = parseInt(container.getAttribute('data-stagger-delay') || '0', 10);
        if (staggerDelay <= 0) return;

        const allAnimated = container.querySelectorAll('[data-animation]');
        const animatedChildren: Element[] = [];
        allAnimated.forEach((el) => {
          if (el.closest('[data-stagger-delay]') === container) {
            animatedChildren.push(el);
          }
        });
        animatedChildren.forEach((child, index) => {
          const baseDelay = parseInt(child.getAttribute('data-animation-delay') || '0', 10);
          child.setAttribute('data-animation-delay', String(baseDelay + index * staggerDelay));
        });
      });

      const elements = document.querySelectorAll('[data-animation]');
      if (elements.length === 0) return;

      // Add pending class to all animated elements so they start hidden
      elements.forEach((el) => {
        el.classList.add(PENDING_CLASS);
      });

      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const el = entry.target as HTMLElement;
            const animationType = el.getAttribute('data-animation');
            const duration = el.getAttribute('data-animation-duration') || '600';
            const delay = el.getAttribute('data-animation-delay') || '0';
            const easing = el.getAttribute('data-animation-easing') || 'ease-out';

            if (!animationType) continue;
            const keyframe = ANIMATION_KEYFRAME_MAP[animationType];
            if (!keyframe) continue;

            el.style.animation = `${keyframe} ${duration}ms ${easing} ${delay}ms both`;
            el.classList.remove(PENDING_CLASS);
            observer.unobserve(el);

            el.addEventListener(
              'animationend',
              () => {
                el.style.animation = '';
              },
              { once: true },
            );
          }
        },
        { threshold: INTERSECTION_THRESHOLD },
      );

      elements.forEach((el) => observer.observe(el));

      // Store observer ref for cleanup
      (window as unknown as Record<string, IntersectionObserver>).__b4AnimObserver = observer;
    }, 50);

    return () => {
      clearTimeout(timeoutId);

      // Disconnect observer
      const observer = (window as unknown as Record<string, IntersectionObserver>).__b4AnimObserver;
      if (observer) {
        observer.disconnect();
        delete (window as unknown as Record<string, IntersectionObserver>).__b4AnimObserver;
      }

      // Remove pending class and animation styles from elements
      const elements = document.querySelectorAll('[data-animation]');
      elements.forEach((el) => {
        (el as HTMLElement).style.animation = '';
        el.classList.remove(PENDING_CLASS);
      });
    };
  }, [previewMode]);
};
